import USStatesWithISOCodes from '@liveauctioneers/caterwaul-components/lib/Intl/USStatesWithISOCodes';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { OptionType, Select } from '@liveauctioneers/hammer-ui-core/select';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useNavigateSearchPageWithParams } from '@/pages/SearchPageSimplified/hooks/useNavigateSearchPageWithParams';
import { CurrentOpenMenu } from '@/pages/SearchPageSimplified/types';
import paramTypes from '@/types/search/enums/paramTypes';
import { FilterAction } from '@/types/SearchFacet';
import { CanadianProvincesWithISOCodes } from '@/utils/address';
import {
    modifyParamsRemovePage,
    modifyParamsRemoveState,
    modifyParamsReplaceState,
    parseQueryParams,
} from '@/utils/queryParams';

type Props = {
    countryCode: string;
    isAllFilters?: boolean;
};

export const StateFilter = ({ countryCode, isAllFilters }: Props) => {
    const { formatMessage } = useIntl();
    const { search } = useLocation();
    const queryParams = parseQueryParams(search);
    const navigateWithParams = useNavigateSearchPageWithParams();
    const currentStateParam = queryParams[paramTypes.STATE_ID] ?? '';

    const keepMenuOpenValue = isAllFilters ? CurrentOpenMenu.AllFilters : CurrentOpenMenu.Location;

    const isUS = countryCode === 'US';
    const labelText = formatMessage({ id: isUS ? 'searchPage.facets.state' : 'searchPage.facets.province' });

    const stateOptions: OptionType[] = useMemo(() => {
        const regions = isUS ? USStatesWithISOCodes : CanadianProvincesWithISOCodes;
        const allLabel = formatMessage({ id: isUS ? 'searchPage.facets.allStates' : 'searchPage.facets.allProvinces' });
        const options = regions.map(({ code, name }) => ({ label: name, value: code }));
        return [{ label: allLabel, value: '' }, ...options];
    }, [isUS, formatMessage]);

    const selectedOption = stateOptions.find((opt) => opt.value === currentStateParam) ?? stateOptions[0];

    const onStateChange = useCallback(
        (option: OptionType | null) => {
            if (!option || option.value === '') {
                let newParams = modifyParamsRemoveState(queryParams);
                newParams = modifyParamsRemovePage(newParams);

                navigateWithParams({
                    checkForPageRemoval: true,
                    currentOpenMenu: keepMenuOpenValue,
                    filterAction: FilterAction.LocationRemoved,
                    params: newParams,
                    preventScrollReset: true,
                });
            } else {
                let newParams = modifyParamsReplaceState(queryParams, String(option.value));
                newParams = modifyParamsRemovePage(newParams);

                navigateWithParams({
                    checkForPageRemoval: true,
                    currentOpenMenu: keepMenuOpenValue,
                    filterAction: FilterAction.LocationChanged,
                    params: newParams,
                    preventScrollReset: true,
                });
            }
        },
        [keepMenuOpenValue, navigateWithParams, queryParams]
    );

    return (
        <div className="mt-xs">
            <Select
                aria-label={formatMessage({
                    id: isUS ? 'searchPage.facets.selectState' : 'searchPage.facets.selectProvince',
                })}
                data-testid="location-state-select"
                instanceId="state-filter"
                labelText={labelText}
                name="stateFilter"
                onChange={onStateChange}
                options={stateOptions}
                small
                usePortalMenu
                value={selectedOption}
            />
        </div>
    );
};
